<div class="stacksets-update-instructions">
  <!-- Setup Mode: StackSets not yet configured -->
  <ng-container *ngIf="isSetupMode">
    <h2>Set up StackSets</h2>
    <p class="intro">
      You've chosen to manage {{ uiService.productName }} access with CloudFormation StackSets. Follow these steps to
      deploy StackSets and grant {{ uiService.productName }} access to your AWS accounts.
    </p>

    <!-- Info Notice for Setup -->
    <div class="info-notice">
      <i class="fa-solid fa-circle-info"></i>
      <div class="notice-content">
        <p>Run these commands from your <strong>management account</strong> to create the StackSets:</p>
        <div class="affected-accounts">
          <strong>Target accounts:</strong>
          <span class="account-tags">
            <code *ngFor="let accountId of uniqueAccountIds">{{ accountId }}</code>
          </span>
        </div>
      </div>
    </div>
  </ng-container>

  <!-- Update Mode: StackSets already configured -->
  <ng-container *ngIf="!isSetupMode">
    <h2>Update StackSets</h2>
    <p class="intro">
      We've enhanced {{ uiService.productName }}, but to get the latest functionality for this application, you'll need
      to update your StackSets.
    </p>

    <!-- Info Notice for Update -->
    <div class="info-notice">
      <i class="fa-solid fa-circle-info"></i>
      <div class="notice-content">
        <p>
          Run the following commands from your <strong>management account</strong> to update the StackSets. This will
          update all stack instances in your StackSets, applying the latest permissions for all
          {{ uiService.productName }} applications at once:
        </p>

        <div class="affected-accounts">
          <strong>Affected accounts:</strong>
          <span class="account-tags">
            <code *ngFor="let accountId of uniqueAccountIds">{{ accountId }}</code>
          </span>
        </div>
      </div>
    </div>
  </ng-container>

  <div class="stackset-names-section">
    <p class="names-intro">
      <i class="fa-solid fa-circle-info"></i>
      <span>Enter your management account ID and StackSet names to generate the update commands:</span>
    </p>
    <div class="name-inputs">
      <div class="name-input-group">
        <label for="update-management-account-id">
          <i class="fa-solid fa-shield-halved"></i>
          Management Account ID
        </label>
        <input
          type="text"
          id="update-management-account-id"
          [(ngModel)]="managementAccountIdInput"
          placeholder="e.g., 123456789012"
        />
      </div>
      <div class="name-input-group">
        <label for="update-primary-stackset-name">
          <i class="fa-solid fa-location-dot"></i>
          Primary Access StackSet Name
        </label>
        <input
          type="text"
          id="update-primary-stackset-name"
          [(ngModel)]="primaryStackSetName"
          placeholder="e.g., ArpioPrimaryAccess"
        />
      </div>
      <div class="name-input-group">
        <label for="update-recovery-stackset-name">
          <i class="fa-solid fa-location-crosshairs"></i>
          Recovery Access StackSet Name
        </label>
        <input
          type="text"
          id="update-recovery-stackset-name"
          [(ngModel)]="recoveryStackSetName"
          placeholder="e.g., ArpioRecoveryAccess"
        />
      </div>
    </div>
  </div>

  <div class="command-group">
    <h4>
      <i class="fa-solid fa-terminal"></i>
      {{ isSetupMode ? 'Create' : 'Update' }} Primary Access StackSet
    </h4>
    <!-- Test Access for Primary -->
    <ng-container *ngIf="sourceIsLatest; else sourceNotLatest">
      <div class="configured-notice">
        <i class="fa-solid fa-circle-check"></i>
        <span>Primary access is up to date</span>
      </div>
    </ng-container>
    <ng-template #sourceNotLatest>
      <div class="notice-block">
        <div class="notice-text">
          <strong *ngIf="sourceRetryText" class="retry-text">{{ sourceRetryText }}</strong>
          <span>After running the update command, allow 1–3 minutes for changes to propagate, then verify.</span>
        </div>
        <button class="arpio-button test-access-btn" [disabled]="isTestingSource" (click)="onTestAccess('source')">
          <ng-container *ngIf="isTestingSource; else notTestingSource">
            <i class="fa-solid fa-spinner fa-spin"></i> TESTING...
          </ng-container>
          <ng-template #notTestingSource>TEST ACCESS</ng-template>
        </button>
      </div>
    </ng-template>
    <div class="command-block">
      <div class="command-actions">
        <button class="copy-btn" [copy-clipboard]="primaryStackSetCommand" (copied)="onCopied('primary')">
          <i
            class="fa-utility fa-semibold"
            [class.fa-copy]="!isCopied('primary')"
            [class.fa-check]="isCopied('primary')"
          ></i>
          {{ isCopied('primary') ? 'Copied!' : 'Copy' }}
        </button>
        <button class="download-btn" (click)="downloadCommand('primary')" title="Download as shell script">
          <i class="fa-solid fa-download"></i>
          Download
        </button>
      </div>
      <pre><code>{{ primaryStackSetCommand }}</code></pre>
    </div>
  </div>

  <div class="command-group">
    <h4>
      <i class="fa-solid fa-terminal"></i>
      {{ isSetupMode ? 'Create' : 'Update' }} Recovery Access StackSet
    </h4>
    <!-- Test Access for Recovery -->
    <ng-container *ngIf="targetIsLatest; else targetNotLatest">
      <div class="configured-notice">
        <i class="fa-solid fa-circle-check"></i>
        <span>Recovery access is up to date</span>
      </div>
    </ng-container>
    <ng-template #targetNotLatest>
      <div class="notice-block">
        <div class="notice-text">
          <strong *ngIf="targetRetryText" class="retry-text">{{ targetRetryText }}</strong>
          <span>After running the update command, allow 1–3 minutes for changes to propagate, then verify.</span>
        </div>
        <button class="arpio-button test-access-btn" [disabled]="isTestingTarget" (click)="onTestAccess('target')">
          <ng-container *ngIf="isTestingTarget; else notTestingTarget">
            <i class="fa-solid fa-spinner fa-spin"></i> TESTING...
          </ng-container>
          <ng-template #notTestingTarget>TEST ACCESS</ng-template>
        </button>
      </div>
    </ng-template>
    <div class="command-block">
      <div class="command-actions">
        <button class="copy-btn" [copy-clipboard]="recoveryStackSetCommand" (copied)="onCopied('recovery')">
          <i
            class="fa-utility fa-semibold"
            [class.fa-copy]="!isCopied('recovery')"
            [class.fa-check]="isCopied('recovery')"
          ></i>
          {{ isCopied('recovery') ? 'Copied!' : 'Copy' }}
        </button>
        <button class="download-btn" (click)="downloadCommand('recovery')" title="Download as shell script">
          <i class="fa-solid fa-download"></i>
          Download
        </button>
      </div>
      <pre><code>{{ recoveryStackSetCommand }}</code></pre>
    </div>
  </div>
</div>
